/**
 * Centralized toast notification hook with i18n support
 *
 * Wraps react-hot-toast with translation capabilities
 *
 * @example
 * ```tsx
 * const toastify = useToast();
 *
 * // Simple success toast
 * toastify.success('saved');
 *
 * // Toast with interpolation
 * toastify.success('campaign.created', { name: campaignName });
 *
 * // Toast with custom message (bypasses translation)
 * toastify.successRaw('Custom message');
 * ```
 */

import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export const useToast = () => {
  const { t } = useTranslation('toasts');

  return {
    /**
     * Show success toast with translation key
     * @param key - Translation key from toasts.json
     * @param data - Optional interpolation data
     */
    success: (key: string, data?: Record<string, any>) => {
      return toast.success(t(key, data));
    },

    /**
     * Show error toast with translation key
     * @param key - Translation key from toasts.json
     * @param data - Optional interpolation data
     */
    error: (key: string, data?: Record<string, any>) => {
      return toast.error(t(key, data));
    },

    /**
     * Show loading toast with translation key
     * @param key - Translation key from toasts.json
     * @param data - Optional interpolation data
     */
    loading: (key: string, data?: Record<string, any>) => {
      return toast.loading(t(key, data));
    },

    /**
     * Show info toast with translation key
     * @param key - Translation key from toasts.json
     * @param data - Optional interpolation data
     */
    info: (key: string, data?: Record<string, any>) => {
      return toast(t(key, data), { icon: 'ℹ️' });
    },

    /**
     * Show success toast with raw message (no translation)
     * Use this for dynamic messages from API responses
     * @param message - Raw message to display
     */
    successRaw: (message: string) => {
      return toast.success(message);
    },

    /**
     * Show error toast with raw message (no translation)
     * Use this for error messages from API responses
     * @param message - Raw message to display
     */
    errorRaw: (message: string) => {
      return toast.error(message);
    },

    /**
     * Show loading toast with raw message (no translation)
     * @param message - Raw message to display
     */
    loadingRaw: (message: string) => {
      return toast.loading(message);
    },

    /**
     * Dismiss a specific toast
     * @param toastId - Toast ID returned from toast methods
     */
    dismiss: (toastId?: string) => {
      return toast.dismiss(toastId);
    },

    /**
     * Promise-based toast for async operations
     * @param promise - Promise to handle
     * @param messages - Translation keys for loading, success, error states
     */
    promise: <T,>(
      promise: Promise<T>,
      messages: {
        loading: string;
        success: string;
        error: string;
      }
    ) => {
      return toast.promise(promise, {
        loading: t(messages.loading),
        success: t(messages.success),
        error: t(messages.error),
      });
    },
  };
};
